import { Slide } from '@/client/courses/types/courses.type';
import { useSortableItem } from '@/hooks/useSortableItem';
import { TrashIcon } from '@/utils/helpers/icons.helper';
import { t } from 'i18next';
import React from 'react';
import styled from 'styled-components';

const SlideCard = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ selected }) =>
    selected ? 'var(--red-pale)' : 'var(--white-main)'};
  border: 2px solid
    ${({ selected }) => (selected ? 'var(--red-main)' : 'var(--gray-medium)')};
  border-radius: var(--xsmall-border-radius);
  margin-right: var(--xsmall-padding);
  padding: var(--medium-padding);
  cursor: pointer;
  position: relative;
  transition: border 0.2s, background 0.2s;
  box-sizing: border-box;
  overflow: hidden;
  flex-shrink: 0;

  &:last-child {
    margin-right: 0;
  }
`;

const SlideCardTitle = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
  margin-bottom: 4px;
`;

const SlideCardNumber = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--black-main);
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  background: ${({ selected }) =>
    selected ? 'var(--red-light-opacity)' : 'var(--beige-main)'};
  border-radius: 5px;
  width: 160px;
  height: 80px;
`;

const DeleteIconContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  z-index: 2;
  background: transparent;
  border-radius: 4px;
  padding: 2px;
  transition: background 0.15s;

  &:hover {
    background: var(--beige-main);
  }

  &:hover svg path {
    fill: var(--red-main);
  }

  svg {
    width: 10px;
  }
`;

const SortableSlideCard: React.FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  const { setNodeRef, dragHandleProps, isDragging, style } = useSortableItem({
    id,
    style: { zIndex: 1 },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...dragHandleProps}
    >
      {children}
    </div>
  );
};

export const SlideTimelineCard: React.FC<{
  slide: Slide;
  index: number;
  isSelected: boolean;
  isSortable?: boolean;
  onSelect: () => void;
  onDelete: () => void;
  isAnyDragging?: boolean;
}> = ({
  slide,
  index,
  isSelected,
  isSortable = false,
  onSelect,
  onDelete,
  isAnyDragging = false,
}) => {
  const handleSelect = () => {
    if (isAnyDragging) return;
    onSelect();
  };

  const cardContent = (
    <SlideCard
      className="slide"
      selected={isSelected}
      style={{ height: isSortable ? '100%' : 'auto' }}
      onClick={handleSelect}
    >
      <DeleteIconContainer
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title={t('content.delete.slide')}
        style={{
          opacity: 1,
          pointerEvents: 'auto',
        }}
      >
        <TrashIcon />
      </DeleteIconContainer>
      <SlideCardTitle>
        {slide.headline ? slide.headline.replace(/<[^>]*>?/g, '') : ''}
      </SlideCardTitle>
      <SlideCardNumber selected={isSelected}>{index + 1}</SlideCardNumber>
    </SlideCard>
  );

  if (isSortable) {
    return (
      <SortableSlideCard key={slide.id} id={slide.id || ''}>
        {cardContent}
      </SortableSlideCard>
    );
  }

  return <>{cardContent}</>;
};
